import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck, Trash2, UsersRound, X } from 'lucide-react';
import { formatDateBR, toDateSP } from '@lira/shared';
import type { AdminUser, AdminUsersResponse } from '@lira/shared';
import { apiDelete, apiGet, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { ConfirmDialog, Skeleton } from '@/components/ui';
import { cn } from '@/lib/cn';

const STATUS_META = {
  pending: { label: 'Pendente', cls: 'bg-alert/15 text-alert' },
  active: { label: 'Ativo', cls: 'bg-income-weak text-income' },
  rejected: { label: 'Recusado', cls: 'bg-expense-weak text-expense' },
} as const;

/** Dígitos → '(11) 96494-0697' (com +cc se vier código do país). */
function formatPhone(raw: string | null): string {
  if (!raw) return '—';
  const s = raw.replace(/\D/g, '');
  let cc = '';
  let local = s;
  if (s.length > 11) {
    cc = `+${s.slice(0, s.length - 11)} `;
    local = s.slice(-11);
  }
  if (local.length === 11) return `${cc}(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `${cc}(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return raw;
}

/** Link wa.me (assume Brasil se não houver código do país). */
function waLink(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.replace(/\D/g, '');
  const full = s.length <= 11 ? `55${s}` : s;
  return `https://wa.me/${full}`;
}

function UserRow({
  u,
  onApprove,
  onReject,
  onDelete,
  busy,
}: {
  u: AdminUser;
  onApprove: (u: AdminUser) => void;
  onReject: (u: AdminUser) => void;
  onDelete: (u: AdminUser) => void;
  busy: boolean;
}) {
  const meta = STATUS_META[u.status];
  const wa = waLink(u.phone);

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-ink">{u.name}</p>
          {u.role === 'admin' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-weak px-2 py-0.5 text-[11px] font-semibold text-primary">
              <ShieldCheck className="size-3" /> Admin
            </span>
          )}
          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', meta.cls)}>
            {meta.label}
          </span>
        </div>
        <p className="truncate text-sm text-muted">
          {u.email} · Cadastro {formatDateBR(toDateSP(u.createdAt))}
        </p>
        <p className="truncate text-[13px] text-muted">
          WhatsApp:{' '}
          {wa ? (
            <a href={wa} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
              {formatPhone(u.phone)}
            </a>
          ) : (
            <span className="text-ink">—</span>
          )}
        </p>
      </div>

      {!u.isSelf && (
        <div className="flex shrink-0 items-center gap-1.5">
          {u.status !== 'active' && (
            <button
              type="button"
              onClick={() => onApprove(u)}
              disabled={busy}
              className="flex h-9 items-center gap-1.5 rounded-full bg-income px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <Check className="size-4" /> Aprovar
            </button>
          )}
          {u.status !== 'rejected' && (
            <button
              type="button"
              onClick={() => onReject(u)}
              disabled={busy}
              aria-label="Recusar"
              title="Recusar acesso"
              className="grid size-9 place-items-center rounded-full text-muted transition-colors hover:bg-alert/10 hover:text-alert disabled:opacity-50"
            >
              <X className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(u)}
            disabled={busy}
            aria-label="Excluir usuário"
            title="Excluir usuário e todos os dados"
            className="grid size-9 place-items-center rounded-full text-muted transition-colors hover:bg-expense-weak hover:text-expense disabled:opacity-50"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      )}
    </li>
  );
}

/** Painel de administração de usuários (só role=admin): aprovar / recusar / excluir. */
export function UsersAdmin() {
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiGet<AdminUsersResponse>('/admin/users'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-users'] });

  const approve = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/users/${id}/approve`),
    onSuccess: () => {
      invalidate();
      toast('Usuário aprovado.', 'success');
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const reject = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/users/${id}/reject`),
    onSuccess: () => {
      invalidate();
      toast('Acesso recusado.', 'success');
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/users/${id}`),
    onSuccess: () => {
      invalidate();
      toast('Usuário excluído.', 'success');
      setConfirmDelete(null);
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const users = usersQuery.data?.users;
  const pendingCount = users?.filter((u) => u.status === 'pending').length ?? 0;
  const busyId =
    approve.isPending ? approve.variables : reject.isPending ? reject.variables : undefined;

  return (
    <div className="rounded-lg bg-surface p-6 shadow-card">
      <div className="flex items-center gap-2">
        <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-primary">
          <UsersRound className="size-4" />
        </span>
        <h2 className="text-lg font-semibold text-ink">Usuários</h2>
        {pendingCount > 0 && (
          <span className="rounded-full bg-alert/15 px-2 py-0.5 text-[11px] font-semibold text-alert">
            {pendingCount} pendente{pendingCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-muted">
        Aprove ou recuse quem se cadastrou. Excluir remove o usuário e todos os dados dele.
      </p>

      <div className="mt-4">
        {usersQuery.isLoading ? (
          <div className="space-y-3" aria-hidden>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : usersQuery.isError ? (
          <p className="py-6 text-center text-sm text-expense">{errorMessage(usersQuery.error)}</p>
        ) : !users || users.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">Nenhum usuário ainda.</p>
        ) : (
          <ul className="divide-y divide-border">
            {users.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                busy={busyId === u.id || (remove.isPending && remove.variables === u.id)}
                onApprove={(x) => approve.mutate(x.id)}
                onReject={(x) => reject.mutate(x.id)}
                onDelete={setConfirmDelete}
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Excluir usuário?"
        message={
          confirmDelete
            ? `Remove ${confirmDelete.name} (${confirmDelete.email}) e TODOS os dados dele (contas, transações, categorias). Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Excluir"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
